import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Target, ArrowLeft, Sparkles, Mail, ChevronDown, ChevronRight,
    Check, Plus, Search, AlertCircle,
} from 'lucide-react';

import { api } from '../api/client.js';
import { AddToPipelineModal } from '../components/AddToPipelineModal.jsx';
import './MatchmakerView.css';

const STORAGE_STARTUP = 'behold.matchmaker.selected_startup';

const LABEL_ORDER = ['High', 'Medium', 'Low', 'Not clear'];
const LABEL_INFO = {
    'High':      { color: 'high',     description: '70+ points. Strong multi-dimensional fit.' },
    'Medium':    { color: 'medium',   description: '40-69 points. Partial fit — worth evaluating.' },
    'Low':       { color: 'low',      description: '1-39 points. Weak fit, likely not a target.' },
    'Not clear': { color: 'unclear',  description: 'Too many data gaps to score reliably.' },
};

export function MatchmakerView() {
    const queryClient = useQueryClient();

    const [startupId, setStartupId] = useState(() => {
        const saved = localStorage.getItem(STORAGE_STARTUP);
        return saved ? Number(saved) : null;
    });

    useEffect(() => {
        if (startupId) localStorage.setItem(STORAGE_STARTUP, String(startupId));
    }, [startupId]);

    const [search, setSearch]             = useState('');
    const [labelFilter, setLabelFilter]   = useState('all');   // 'all' | 'High' | 'Medium' | etc.
    const [hideExisting, setHideExisting] = useState(true);
    const [expanded, setExpanded]         = useState(new Set());
    const [pipelineInvestor, setPipelineInvestor] = useState(null);

    const { data: startups } = useQuery({
        queryKey: ['startups'],
        queryFn: () => api.get('/startups'),
    });

    const { data: matches, isLoading, error } = useQuery({
        queryKey: ['matchmaker', startupId],
        queryFn: () => api.get(`/matchmaker/${startupId}`),
        enabled: startupId != null,
    });

    const selectedStartup = startups?.find(s => s.id === startupId);

    // ---- Filter + search ----
    const filteredResults = useMemo(() => {
        if (!matches?.results) return [];
        let list = matches.results;

        // Drop "No match" by default — too much noise. User can opt in via labelFilter.
        if (labelFilter === 'all') {
            list = list.filter(r => r.label !== 'No match');
        } else {
            list = list.filter(r => r.label === labelFilter);
        }

        if (hideExisting) {
            list = list.filter(r => !r.already_in_pipeline);
        }

        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(r =>
                (r.investor_name || '').toLowerCase().includes(q) ||
                (r.primary_contact_name || '').toLowerCase().includes(q) ||
                (r.primary_contact_email || '').toLowerCase().includes(q)
            );
        }

        return list;
    }, [matches, labelFilter, hideExisting, search]);

    const toggleExpand = (id) => {
        setExpanded(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (!startups) {
        return <div className="mm-loading"><span className="spinner" /></div>;
    }

    // ---- Picker screen ----
    if (startupId == null) {
        return (
            <div className="mm">
                <header className="mm-topbar">
                    <h1 className="display mm-title">Matchmaker</h1>
                </header>
                <div className="mm-picker-empty">
                    <div className="mm-picker-inner">
                        <Sparkles size={32} strokeWidth={1.25} className="mm-picker-icon"/>
                        <h2>Choose a portfolio company</h2>
                        <p>Matches are computed against one startup at a time. Pick the one you're raising for.</p>
                        <div className="mm-picker-grid">
                            {startups.map(s => (
                                <button key={s.id} className="mm-picker-card"
                                        onClick={() => setStartupId(s.id)}>
                                    <span className="mm-picker-name">{s.name}</span>
                                    {s.sector && <span className="mm-picker-sector">{s.sector}</span>}
                                    {!s.sector && !s.stage && (
                                        <span className="mm-picker-warning">
                                            <AlertCircle size={11} strokeWidth={1.75}/>
                                            Profile incomplete
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const profileComplete = selectedStartup && selectedStartup.sector && selectedStartup.stage
                            && selectedStartup.geo_focus;

    return (
        <div className="mm">
            {/* Topbar */}
            <header className="mm-topbar">
                <div className="mm-topbar-left">
                    <h1 className="display mm-title">Matchmaker</h1>
                    <select className="kanban-startup-select" value={startupId}
                            onChange={(e) => setStartupId(Number(e.target.value))}>
                        {startups.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button className="kanban-change-startup" onClick={() => setStartupId(null)}>
                        <ArrowLeft size={13} strokeWidth={2}/> All portfolios
                    </button>
                </div>

                {selectedStartup && (
                    <div className="mm-meta">
                        {[selectedStartup.sector, selectedStartup.stage, selectedStartup.geo_focus]
                            .filter(Boolean).join(' · ')}
                    </div>
                )}
            </header>

            {/* Profile-incomplete warning */}
            {selectedStartup && !profileComplete && (
                <div className="mm-warning">
                    <AlertCircle size={14} strokeWidth={2}/>
                    <div>
                        <strong>{selectedStartup.name}'s profile is incomplete.</strong>
                        &nbsp;Fill in sector, stage, and geographic focus in <em>Settings → Portfolio</em> to get meaningful matches.
                    </div>
                </div>
            )}

            {/* Summary + filters */}
            {matches && (
                <section className="mm-controls">
                    <div className="mm-summary">
                        <button
                            className={`mm-summary-chip${labelFilter === 'all' ? ' is-active' : ''}`}
                            onClick={() => setLabelFilter('all')}
                        >
                            All <span className="mm-summary-count">{matches.summary.high + matches.summary.medium + matches.summary.low + matches.summary.not_clear}</span>
                        </button>
                        {LABEL_ORDER.map(label => {
                            const key = label === 'Not clear' ? 'not_clear' : label.toLowerCase();
                            const count = matches.summary[key] || 0;
                            return (
                                <button key={label}
                                        className={`mm-summary-chip mm-chip-${LABEL_INFO[label].color}${labelFilter === label ? ' is-active' : ''}`}
                                        onClick={() => setLabelFilter(label)}
                                        title={LABEL_INFO[label].description}>
                                    {label} <span className="mm-summary-count">{count}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mm-filters">
                        <div className="mm-search">
                            <Search size={13} strokeWidth={1.75} className="mm-search-icon"/>
                            <input type="text" value={search}
                                   onChange={(e) => setSearch(e.target.value)}
                                   placeholder="Filter by name or email..."
                                   className="mm-search-input"/>
                        </div>
                        <label className="mm-checkbox-label">
                            <input type="checkbox" checked={hideExisting}
                                   onChange={(e) => setHideExisting(e.target.checked)}/>
                            <span>Hide investors already on the board</span>
                        </label>
                    </div>
                </section>
            )}

            {/* Results */}
            <div className="mm-body">
                {isLoading && <div className="mm-loading"><span className="spinner"/></div>}
                {error && <div className="panel-error">Couldn't compute matches: {error.message}</div>}

                {matches && filteredResults.length === 0 && (
                    <div className="mm-empty">
                        <Sparkles size={28} strokeWidth={1.25} style={{ opacity: 0.4 }}/>
                        <p>No matches for these filters.</p>
                    </div>
                )}

                <ul className="mm-results">
                    {filteredResults.map(r => (
                        <MatchRow
                            key={r.investor_id}
                            match={r}
                            expanded={expanded.has(r.investor_id)}
                            onToggle={() => toggleExpand(r.investor_id)}
                            onAddToPipeline={() => setPipelineInvestor({
                                id: r.investor_id,
                                name: r.investor_name,
                            })}
                        />
                    ))}
                </ul>
            </div>

            {pipelineInvestor && (
                <AddToPipelineModal
                    investor={pipelineInvestor}
                    onClose={() => setPipelineInvestor(null)}
                    onCreated={() => {
                        queryClient.invalidateQueries({ queryKey: ['matchmaker', startupId] });
                        setPipelineInvestor(null);
                    }}
                />
            )}
        </div>
    );
}

/* ==========================================================================
 * MatchRow — one investor result with expandable breakdown
 * ========================================================================== */
function MatchRow({ match, expanded, onToggle, onAddToPipeline }) {
    const info = LABEL_INFO[match.label] || LABEL_INFO['Low'];

    return (
        <li className={`mm-row mm-row-${info.color}${expanded ? ' is-expanded' : ''}`}>
            <div className="mm-row-main" onClick={onToggle}>
                <button className="mm-row-caret">
                    {expanded
                        ? <ChevronDown size={14} strokeWidth={2}/>
                        : <ChevronRight size={14} strokeWidth={2}/>}
                </button>

                <div className="mm-row-score">
                    <div className={`mm-score-badge mm-score-${info.color}`}>
                        {match.label === 'Not clear' ? '?' : match.score}
                    </div>
                    <div className="mm-score-label">{match.label}</div>
                </div>

                <div className="mm-row-info">
                    <div className="mm-row-name">
                        {match.investor_name}
                        {match.already_in_pipeline && (
                            <span className="mm-row-pipeline-pill">
                                <Check size={10} strokeWidth={2}/> On board
                            </span>
                        )}
                    </div>
                    {match.primary_contact_name && (
                        <div className="mm-row-contact">
                            {match.primary_contact_email ? (
                                <a href={`mailto:${match.primary_contact_email}`}
                                   className="mm-row-contact-link"
                                   onClick={(e) => e.stopPropagation()}>
                                    <Mail size={11} strokeWidth={1.75}/>
                                    {match.primary_contact_name}
                                </a>
                            ) : match.primary_contact_name}
                        </div>
                    )}
                </div>

                <div className="mm-row-actions" onClick={(e) => e.stopPropagation()}>
                    {!match.already_in_pipeline && match.label !== 'No match' && (
                        <button className="mm-add-btn" onClick={onAddToPipeline}>
                            <Plus size={12} strokeWidth={2}/> Add to deals
                        </button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="mm-breakdown">
                    <h4>Why this score?</h4>
                    <ul className="mm-breakdown-list">
                        {Object.entries(match.breakdown).map(([field, detail]) => (
                            <li key={field} className={`mm-breakdown-item${detail.unknown ? ' is-unknown' : ''}`}>
                                <span className="mm-breakdown-field">{fieldLabel(field)}</span>
                                <span className="mm-breakdown-bar">
                                    <span className="mm-breakdown-bar-fill"
                                          style={{ width: `${(detail.score / detail.max) * 100}%` }}/>
                                </span>
                                <span className="mm-breakdown-score mono">
                                    {detail.score} / {detail.max}
                                </span>
                                <span className="mm-breakdown-reason">{detail.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
}

function fieldLabel(field) {
    return {
        sector: 'Sector',
        stage:  'Stage',
        ticket: 'Ticket sweetspot',
        geo:    'Geography',
    }[field] || field;
}
